import { LogRecordState } from '@cf-shared/data-model/user-log';
import { WorkoutLoadMode } from 'models/workout-load-mode';

/**
 * Which actions the Training page's workout options menu offers.
 *
 * This lives outside the page component so it can be unit-tested without
 * Angular, the same way `pages/settings-page/settings-section-access.ts` and
 * `pages/subscribe/purchase-offer-policy.ts` do it. The page itself pulls in
 * the whole Angular dependency graph and cannot be loaded by the plain-Node
 * test runner.
 *
 * Why it exists at all (deferred finding `df-3d5d440d3a78`): the decision used
 * to sit inside `getWorkoutOptionButtons` in `training.ts`, mixed in with the
 * translated labels and the tap handlers, so no test could reach it. Every
 * change to which actions a workout offers was checked only by the compiler and
 * by whoever read the diff. That is what let this page and the Home page
 * disagree for as long as they did about offering "Skip" on a workout the user
 * had already finished (`df-f14e97e159a5`, #465) -- a wrong "Skip" there hid the
 * finished session, dropped it out of the completed-workout counts the nutrition
 * plan is built from, and left the stored workout saying "skipped" while every
 * set in it said "done".
 *
 * The page keeps the parts that need Angular: the translated label for each kind
 * and the handler behind it.
 *
 * Home has its own copy of a similar decision, in
 * `components/cs-day-items/day-item-actions-policy.ts`. It is NOT shared with
 * this one -- it also covers meals, weigh-ins and weekly checkups -- so the two
 * are separate rules, but both are now unit-tested, including the rule that a
 * finished workout is never offered "Skip".
 */

/** One action the workout options menu can offer. */
export enum WorkoutOptionKind {
  /** Undo a skip, putting the workout back on the schedule. */
  Restore = 'Restore',
  /** Mark the workout as skipped. */
  Skip = 'Skip',
  /** Swap this day's workout for another one from the plan. */
  ChangeWorkout = 'ChangeWorkout',
  /** Open the plan editor on this workout. */
  EditWorkout = 'EditWorkout',
}

/** Everything the decision depends on. */
export interface WorkoutOptionsState {
  /**
   * The stored state of the workout being shown, or undefined when the day
   * being viewed has no workout at all.
   */
  workoutState: LogRecordState | undefined;
  /** How the page is displaying this workout. */
  loadMode: WorkoutLoadMode;
  /** Whether the day being viewed is today. */
  isViewingToday: boolean;
}

/**
 * Which actions to offer, in the order they appear in the menu.
 *
 * The rules, in the order they are applied:
 *
 * 1. A skipped workout offers "Restore" -- but only when the day being viewed
 *    is NOT today, because on today the page draws that button directly in the
 *    template instead of putting it in the menu.
 * 2. A workout that is neither skipped nor already finished offers "Skip".
 *    A finished one deliberately does not: see the header comment.
 * 3. A workout the user may still change offers "Different Workout". Those are
 *    the current workout and one scheduled soon (`SoftFuture`); a past workout,
 *    a far-future one or a template does not.
 * 4. Every workout but a far-future one offers "Edit Workout", because on a
 *    far-future workout editing is the page's primary button rather than a
 *    menu entry.
 */
export function getWorkoutOptionKinds(state: WorkoutOptionsState): WorkoutOptionKind[] {
  const kinds: WorkoutOptionKind[] = [];

  // if skipped and not today, offer "Restore"
  // (today shows the button directly in the template)
  if (state.workoutState === LogRecordState.Skipped) {
    if (!state.isViewingToday) {
      kinds.push(WorkoutOptionKind.Restore);
    }
  } else if (state.workoutState !== LogRecordState.Done) {
    // offer skip for workouts that are neither skipped nor already finished
    kinds.push(WorkoutOptionKind.Skip);
  }

  // offer "Different Workout" for editable workouts
  if (state.loadMode === WorkoutLoadMode.Current || state.loadMode === WorkoutLoadMode.SoftFuture) {
    kinds.push(WorkoutOptionKind.ChangeWorkout);
  }

  // offer "Edit Workout" except on HardFuture, where it is the primary button
  if (state.loadMode !== WorkoutLoadMode.HardFuture) {
    kinds.push(WorkoutOptionKind.EditWorkout);
  }

  return kinds;
}
